<template>
  <Wrap>
    <h1 v-if="titleRes">{{ titleRes }}</h1>
    <SkeletonItem
      v-else
      class="title-skeleton"
    />
    <div
      v-if="subtitle"
      class="subtitle"
    >
      {{ displaySubtitle
      }}<span
        v-if="isOverflow && !isViewMore"
        class="more"
        @click.stop="useShowMore"
        >Ещё</span
      >
    </div>
    <SkeletonItem
      v-else
      class="subtitle-skeleton"
    />
  </Wrap>
</template>

<script lang="ts" setup>
  import { SkeletonItem } from '@experience/core-skeleton-lib';
  import { computed, ref } from 'vue';

  import Wrap from '@/shared/ContentWrap.vue';
  import { useScreenSize } from '@/shared/utils/breakpoints';

  const props = defineProps<{
    title: string;
    subtitle: string;
  }>();

  const breakpoints = useScreenSize();
  const isViewMore = ref(false);

  const titleArr = computed(() => props.title.split(':'));
  const isMobileOrTablet = computed(() => breakpoints.isMobile.value || breakpoints.isTablet.value);
  const titleRes = computed(() => (isMobileOrTablet.value ? titleArr.value[0] : props.title));

  const charLimit = computed(() => {
    if (breakpoints.isMobile.value) {
      return 100;
    }
    if (breakpoints.isTablet.value) {
      return 150;
    }

    return 180;
  });

  const isOverflow = computed(() => props.subtitle.length > charLimit.value);

  const displaySubtitle = computed(() => {
    if (!isOverflow.value || isViewMore.value) {
      return props.subtitle;
    }
    const text = props.subtitle;
    const cut = text.slice(0, charLimit.value);
    const lastSpace = cut.lastIndexOf(' ');

    return (lastSpace > 0 ? cut.slice(0, lastSpace) : cut) + '...';
  });

  const useShowMore = () => {
    isViewMore.value = true;
  };
</script>

<style lang="scss" scoped>
  @use '@experience/design-tokens/typography' as legacy-typography;
  @use '@experience/shared/dist/assets/adaptivity' as legacy-adaptivity;
  @use '@/shared/styles/global/sights-common';

  h1,
  .title-skeleton {
    max-width: 1000px;
    margin-top: 32px;
    word-break: auto-phrase;

    @include legacy-typography.base-900-850-extra;

    @include legacy-adaptivity.tablet {
      margin-top: 24px;

      @include legacy-typography.base-800-750-extra;
    }

    @include legacy-adaptivity.mobile {
      hyphens: auto;
    }
  }

  .subtitle,
  .subtitle-skeleton {
    max-width: 800px;
    margin-top: var(--alias-space-16);

    &::first-letter {
      text-transform: uppercase;
    }

    @include legacy-typography.base-500-500-regular;

    @include legacy-adaptivity.tablet {
      margin-top: var(--alias-space-8);
    }
  }

  .title-skeleton {
    height: 88px;

    @include sights-common.skeleton-radius;

    @include legacy-adaptivity.tablet {
      height: 72px;
    }
  }

  .subtitle-skeleton {
    height: 84px;

    @include sights-common.skeleton-radius;
  }

  .more {
    text-decoration-line: underline;
    text-decoration-style: solid;
    text-decoration-color: var(--alias-color-text-subtler);
    text-decoration-skip-ink: none;
    cursor: pointer;

    &:hover {
      color: var(--alias-color-link-default-hovered);
      text-decoration-color: var(--alias-color-link-default-hovered);
    }
  }
</style>
